import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store';
import { authAPI, boardAPI } from '../../api';
import './Layout.css';

interface Props {
  children: React.ReactNode;
}

const COLORS = ['#3498db', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c', '#e67e22'];
const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
const IMPORTANCE_OPTIONS = [
  { value: 'high', label: '상 (7일전~당일 알림)', color: '#e74c3c' },
  { value: 'mid',  label: '중 (2일전~당일 알림)', color: '#f39c12' },
  { value: 'low',  label: '하 (알림 없음)',        color: '#3498db' },
];

// 로그인 시 중요 일정 알림 팝업
const ImportanceAlert: React.FC<{ events: any[]; onClose: () => void }> = ({ events, onClose }) => {
  if (events.length === 0) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'white', borderRadius: 16, width: 400, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 800, fontSize: 15 }}>📢 중요 일정 알림</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'var(--text-muted)' }}>✕</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {events.map(ev => (
            <div key={ev.id} style={{ padding: '10px 14px', borderRadius: 10, background: ev.color + '15', border: `1px solid ${ev.color}40`, borderLeft: `4px solid ${ev.color}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 700, background: ev.color, color: 'white', padding: '1px 7px', borderRadius: 10 }}>
                  {ev.importance === 'high' ? '🔴 중요' : '🟡 알림'}
                </span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>📅 {ev.date}</span>
              </div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{ev.title}</div>
              {ev.content && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{ev.content}</div>}
            </div>
          ))}
        </div>
        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-primary btn-sm" onClick={onClose}>확인</button>
        </div>
      </div>
    </div>
  );
};

const CalendarPopup: React.FC<{ onClose: () => void; isAdmin: boolean; userId: number | null }> = ({ onClose, isAdmin, userId }) => {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [events, setEvents] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addTitle, setAddTitle] = useState('');
  const [addContent, setAddContent] = useState('');
  const [addColor, setAddColor] = useState('#3498db');
  const [addGlobal, setAddGlobal] = useState(false);
  const [addImportance, setAddImportance] = useState<'high'|'mid'|'low'>('low');

  const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;

  const load = useCallback(async () => {
    try {
      const res = await boardAPI.calendar(year, month);
      setEvents(res.data);
    } catch { setEvents([]); }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const prevMonth = () => { if (month === 1) { setYear(y=>y-1); setMonth(12); } else setMonth(m=>m-1); setSelectedDate(null); };
  const nextMonth = () => { if (month === 12) { setYear(y=>y+1); setMonth(1); } else setMonth(m=>m+1); setSelectedDate(null); };

  const getDateStr = (day: number) => `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
  const getEventsForDay = (day: number) => events.filter(e => e.date === getDateStr(day));

  const handleAddEvent = async () => {
    if (!addTitle.trim() || !selectedDate) return;
    try {
      await boardAPI.createEvent({ date: selectedDate, title: addTitle, content: addContent, color: addColor, is_global: addGlobal, importance: addImportance });
      setAddTitle(''); setAddContent(''); setAddColor('#3498db'); setAddGlobal(false); setAddImportance('low');
      setShowAddForm(false);
      load();
    } catch { alert('등록 실패'); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    try { await boardAPI.deleteEvent(id); load(); } catch { alert('삭제 실패'); }
  };

  const firstDay = new Date(year, month-1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const selectedEvents = selectedDate ? events.filter(e => e.date === selectedDate) : [];

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', padding: '60px 16px 0 0', pointerEvents: 'none' }}>
      <div style={{ width: 500, background: 'white', borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 80px)', pointerEvents: 'all', border: '1px solid var(--border)' }}>

        {/* 팝업 헤더 */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={prevMonth} style={{ border: '1px solid var(--border)', background: 'white', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', fontSize: 12 }}>◀</button>
            <span style={{ fontWeight: 800, fontSize: 15 }}>{year}년 {month}월</span>
            <button onClick={nextMonth} style={{ border: '1px solid var(--border)', background: 'white', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', fontSize: 12 }}>▶</button>
            <button onClick={() => { setYear(today.getFullYear()); setMonth(today.getMonth()+1); }}
              style={{ border: '1px solid var(--border)', background: 'white', borderRadius: 6, padding: '2px 8px', cursor: 'pointer', fontSize: 11 }}>오늘</button>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'var(--text-muted)', lineHeight: 1 }}>✕</button>
        </div>

        {/* 캘린더 그리드 */}
        <div style={{ padding: '10px 12px', flexShrink: 0 }}>
          {/* 요일 헤더 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 4 }}>
            {WEEKDAYS.map((d, i) => (
              <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: i===0?'#e74c3c':i===6?'#3498db':'var(--text-muted)', padding: '4px 0' }}>{d}</div>
            ))}
          </div>
          {/* 날짜 그리드 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`e${i}`} style={{ background: '#f8fafc', minHeight: 64, borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }} />
            ))}
            {Array.from({ length: daysInMonth }, (_, i) => i+1).map(day => {
              const dateStr = getDateStr(day);
              const dayEvents = getEventsForDay(day);
              const isToday = dateStr === todayStr;
              const isSelected = dateStr === selectedDate;
              const dow = (firstDay + day - 1) % 7;
              const col = (firstDay + day - 1) % 7;
              const isLastCol = col === 6;
              const row = Math.floor((firstDay + day - 1) / 7);
              const totalRows = Math.ceil((firstDay + daysInMonth) / 7);
              const isLastRow = row === totalRows - 1;
              return (
                <div key={day} onClick={() => setSelectedDate(isSelected ? null : dateStr)}
                  style={{
                    background: isSelected ? '#dbeafe' : isToday ? '#eff6ff' : 'white',
                    minHeight: 64, padding: '4px 3px', cursor: 'pointer',
                    borderRight: isLastCol ? 'none' : '1px solid var(--border)',
                    borderBottom: isLastRow ? 'none' : '1px solid var(--border)',
                    outline: isSelected ? '2px solid var(--primary)' : 'none',
                    outlineOffset: -2, overflow: 'hidden',
                  }}>
                  <div style={{ width: 22, height: 22, borderRadius: '50%', background: isToday ? 'var(--primary)' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: isToday ? 'white' : dow===0 ? '#e74c3c' : dow===6 ? '#3498db' : 'var(--text-primary)', marginBottom: 2, flexShrink: 0 }}>
                    {day}
                  </div>
                  {dayEvents.slice(0, 2).map(ev => (
                    <div key={ev.id} style={{ fontSize: 9, padding: '1px 3px', borderRadius: 2, background: ev.color+'22', color: ev.color, borderLeft: `2px solid ${ev.color}`, marginBottom: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>
                      {ev.title}
                    </div>
                  ))}
                  {dayEvents.length > 2 && <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>+{dayEvents.length-2}</div>}
                </div>
              );
            })}
          </div>
        </div>

        {/* 선택 날짜 일정 */}
        {selectedDate && (
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', borderTop: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', flexShrink: 0 }}>
              <span style={{ fontSize: 13, fontWeight: 700 }}>📋 {selectedDate}</span>
              <button className="btn btn-primary btn-sm" onClick={() => setShowAddForm(!showAddForm)}>+ 추가</button>
            </div>

            {/* 일정 추가 폼 */}
            {showAddForm && (
              <div style={{ padding: '0 16px 10px', flexShrink: 0, borderBottom: '1px solid var(--border)' }}>
                <input className="form-control" placeholder="일정 제목" value={addTitle}
                  onChange={e => setAddTitle(e.target.value)} style={{ marginBottom: 6, fontSize: 13 }} />
                {/* 내용 - textarea로 변경 */}
                <textarea className="form-control" placeholder="내용 (선택)" value={addContent}
                  onChange={e => setAddContent(e.target.value)}
                  rows={3} style={{ marginBottom: 6, fontSize: 12, resize: 'vertical' }} />
                {/* 중요도 */}
                <div style={{ marginBottom: 6 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }}>중요도</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {IMPORTANCE_OPTIONS.map(opt => (
                      <button key={opt.value} onClick={() => { setAddImportance(opt.value as any); setAddColor(opt.color); }}
                        style={{ padding: '3px 10px', borderRadius: 20, border: `1.5px solid ${opt.color}`, background: addImportance === opt.value ? opt.color : 'white', color: addImportance === opt.value ? 'white' : opt.color, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                        {opt.value === 'high' ? '상' : opt.value === 'mid' ? '중' : '하'}
                      </button>
                    ))}
                  </div>
                </div>
                {/* 색상 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                  {COLORS.map(c => (
                    <button key={c} onClick={() => setAddColor(c)} style={{ width: 20, height: 20, borderRadius: '50%', background: c, border: 'none', cursor: 'pointer', outline: addColor===c ? `3px solid ${c}` : 'none', outlineOffset: 2, transform: addColor===c ? 'scale(1.2)' : 'scale(1)' }} />
                  ))}
                </div>
                {isAdmin && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', marginBottom: 6 }}>
                    <input type="checkbox" checked={addGlobal} onChange={e => setAddGlobal(e.target.checked)} />
                    🌐 전체 공개
                  </label>
                )}
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-primary btn-sm" onClick={handleAddEvent}>등록</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setShowAddForm(false)}>취소</button>
                </div>
              </div>
            )}

            {/* 일정 목록 */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px' }}>
              {selectedEvents.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: '16px 0' }}>등록된 일정이 없습니다.</div>
              ) : selectedEvents.map(ev => (
                <div key={ev.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px', background: 'var(--gray-50)', borderRadius: 8, marginBottom: 6, borderLeft: `3px solid ${ev.color}` }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {ev.title}
                      {ev.is_global && <span style={{ fontSize: 10, background: '#dbeafe', color: '#1d4ed8', padding: '1px 5px', borderRadius: 8, fontWeight: 700 }}>전체</span>}
                      {ev.importance === 'high' && <span style={{ fontSize: 10, background: '#fee2e2', color: '#e74c3c', padding: '1px 5px', borderRadius: 8, fontWeight: 700 }}>상</span>}
                      {ev.importance === 'mid' && <span style={{ fontSize: 10, background: '#fef3c7', color: '#f39c12', padding: '1px 5px', borderRadius: 8, fontWeight: 700 }}>중</span>}
                    </div>
                    {ev.content && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{ev.content}</div>}
                  </div>
                  {(isAdmin || ev.author === userId) && (
                    <button onClick={() => handleDelete(ev.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, opacity: 0.5, padding: 0, flexShrink: 0 }}>🗑️</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Layout: React.FC<Props> = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAdmin, isLoggedIn, logout, setAuth } = useAuthStore();
  const [menuOpen, setMenuOpen] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const [alertEvents, setAlertEvents] = useState<any[]>([]);
  const [showAlert, setShowAlert] = useState(false);

  // ✅ 로그인/회원가입 모달
  const [authModal, setAuthModal] = useState<'login' | 'register' | null>(null);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ username: '', password: '', password_confirm: '', email: '' });
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  // ✅ ?login=1 파라미터 감지 시 자동으로 모달 열기
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('login') === '1' && !isLoggedIn) {
      setAuthModal('login');
      // URL에서 파라미터 제거
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [isLoggedIn]);

  const openLogin = () => { setAuthModal('login'); setAuthError(''); setLoginForm({ username: '', password: '' }); };
  const openRegister = () => { setAuthModal('register'); setAuthError(''); setRegisterForm({ username: '', password: '', password_confirm: '', email: '' }); };
  const closeAuth = () => { setAuthModal(null); setAuthError(''); };

  const handleLogin = async () => {
    if (!loginForm.username || !loginForm.password) { setAuthError('아이디와 비밀번호를 입력해주세요.'); return; }
    setAuthLoading(true);
    try {
      const res = await authAPI.login(loginForm.username, loginForm.password);
      setAuth(res.data.user, res.data.token);
      closeAuth();
    } catch (e: any) {
      setAuthError(e.response?.data?.error || '로그인에 실패했습니다.');
    }
    setAuthLoading(false);
  };

  const handleRegister = async () => {
    if (!registerForm.username || !registerForm.password) { setAuthError('아이디와 비밀번호를 입력해주세요.'); return; }
    if (registerForm.password !== registerForm.password_confirm) { setAuthError('비밀번호가 일치하지 않습니다.'); return; }
    setAuthLoading(true);
    try {
      await authAPI.register(registerForm);
      alert('회원가입이 완료되었습니다.\n관리자 승인 후 로그인이 가능합니다.');
      openLogin();
    } catch (e: any) {
      setAuthError(e.response?.data?.error || '회원가입에 실패했습니다.');
    }
    setAuthLoading(false);
  };

  // 로그인 시 중요 일정 알림 체크 - 세션당 1번만
  useEffect(() => {
    if (!isLoggedIn) {
      // 로그아웃 시 세션 플래그 초기화
      sessionStorage.removeItem('alertShown');
      return;
    }
    // 이미 이번 세션에 알림 표시했으면 스킵
    if (sessionStorage.getItem('alertShown')) return;

    const checkImportantEvents = async () => {
      try {
        const today = new Date();
        const year = today.getFullYear();
        const month = today.getMonth() + 1;
        const [res1, res2] = await Promise.all([
          boardAPI.calendar(year, month),
          boardAPI.calendar(month === 12 ? year+1 : year, month === 12 ? 1 : month+1),
        ]);
        const allEvents = [...res1.data, ...res2.data];

        const important = allEvents.filter(ev => {
          if (!ev.importance || ev.importance === 'low') return false;
          const evDate = new Date(ev.date);
          const diff = Math.ceil((evDate.getTime() - today.setHours(0,0,0,0)) / (1000*60*60*24));
          if (ev.importance === 'high') return diff >= 0 && diff <= 7;
          if (ev.importance === 'mid') return diff >= 0 && diff <= 2;
          return false;
        });

        // 알림 표시 후 세션 플래그 설정
        sessionStorage.setItem('alertShown', 'true');
        if (important.length > 0) {
          setAlertEvents(important);
          setShowAlert(true);
        }
      } catch {}
    };
    checkImportantEvents();
  }, [isLoggedIn]);

  const handleLogout = async () => {
    try { await authAPI.logout(); } catch (e) {}
    logout();
    navigate('/');
  };

  return (
    <div className="layout">
      {/* 헤더 */}
      <header className="header">
        <div className="header-left">
          <button className="header-logo" onClick={() => navigate('/')}>
            <div className="header-logo-icon">M</div>
            <span className="header-logo-text">Mohabwe</span>
          </button>
        </div>

        <div className="header-center">
          <nav className="header-nav">
            <button className="nav-item" onClick={() => navigate('/')}>홈</button>
            {isLoggedIn && (
              <>
                <button className="nav-item" onClick={() => navigate('/exam')}>문제풀기</button>
                <button className="nav-item" onClick={() => navigate('/board')}>게시판</button>
                {/* 캘린더 버튼 - 크게 */}
                <button className="nav-item" onClick={() => setCalOpen(!calOpen)}
                  style={{ fontSize: 20, padding: '4px 8px', lineHeight: 1 }}
                  title="캘린더">📅</button>
              </>
            )}
          </nav>
        </div>

        <div className="header-right">
          {isLoggedIn ? (
            <>
              {isAdmin && (
                <button className="btn btn-sm"
                  style={{ background: 'linear-gradient(135deg, #7c3aed, #2563eb)', color: 'white' }}
                  onClick={() => navigate('/admin')}>
                  ⚙️ 관리자
                </button>
              )}
              <span style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 600 }}>
                {user?.username}
              </span>
              {/* ✅ 마이페이지 버튼 */}
              <button className="btn btn-ghost btn-sm"
                onClick={() => navigate('/mypage')}
                style={{ fontSize: 12 }}>
                👤 마이페이지
              </button>
              <div className="header-user">
                <div className="user-avatar" onClick={() => setMenuOpen(!menuOpen)}>
                  {user?.username?.charAt(0).toUpperCase()}
                </div>
                {menuOpen && (
                  <div className="user-menu">
                    <div className="user-menu-name">{user?.username}</div>
                    <div className="user-menu-status">
                      {isAdmin ? '👑 관리자' : '✅ 일반회원'}
                    </div>
                  </div>
                )}
              </div>
              <button className="btn btn-ghost btn-sm" onClick={handleLogout}>
                🚪 로그아웃
              </button>
            </>
          ) : (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn btn-ghost btn-sm" onClick={openLogin}>🔑 로그인</button>
              <button className="btn btn-primary btn-sm" onClick={openRegister}>✏️ 회원가입</button>
            </div>
          )}
        </div>
      </header>

      {/* 메인 컨텐츠 */}
      <main className="main-content">
        {children}
      </main>

      {/* 중요 일정 알림 팝업 */}
      {showAlert && <ImportanceAlert events={alertEvents} onClose={() => setShowAlert(false)} />}

      {/* 캘린더 팝업 */}
      {calOpen && isLoggedIn && (
        <CalendarPopup
          onClose={() => setCalOpen(false)}
          isAdmin={isAdmin}
          userId={user?.id || null}
        />
      )}

      {/* ✅ 로그인/회원가입 모달 */}
      {authModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 3000,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onMouseDown={closeAuth}>
          <div style={{
            background: 'white', borderRadius: 16, width: 380,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            overflow: 'hidden',
          }} onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>

            {/* 탭 헤더 */}
            <div style={{ display: 'flex', borderBottom: '1px solid var(--border)' }}>
              <button onClick={openLogin} style={{
                flex: 1, padding: '14px', border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: 14,
                background: authModal === 'login' ? 'var(--primary)' : 'var(--gray-50)',
                color: authModal === 'login' ? 'white' : 'var(--text-muted)',
              }}>🔑 로그인</button>
              <button onClick={openRegister} style={{
                flex: 1, padding: '14px', border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: 14,
                background: authModal === 'register' ? 'var(--primary)' : 'var(--gray-50)',
                color: authModal === 'register' ? 'white' : 'var(--text-muted)',
              }}>✏️ 회원가입</button>
              <button onClick={closeAuth} style={{
                padding: '14px 16px', border: 'none', background: 'var(--gray-50)',
                cursor: 'pointer', fontSize: 16, color: 'var(--text-muted)',
              }}>✕</button>
            </div>

            <div style={{ padding: '24px' }}>
              {/* 로고 */}
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div style={{ width: 48, height: 48, borderRadius: 12, background: 'var(--primary)', color: 'white', fontSize: 22, fontWeight: 900, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>M</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Mohabwe</div>
              </div>

              {/* 에러 메시지 */}
              {authError && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#dc2626', marginBottom: 16 }}>
                  ⚠️ {authError}
                </div>
              )}

              {/* 로그인 폼 */}
              {authModal === 'login' && (
                <>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>아이디</label>
                    <input className="form-control" placeholder="아이디를 입력하세요"
                      value={loginForm.username}
                      onChange={e => setLoginForm({ ...loginForm, username: e.target.value })}
                      onKeyDown={e => e.key === 'Enter' && handleLogin()}
                      autoFocus />
                  </div>
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>비밀번호</label>
                    <input className="form-control" type="password" placeholder="비밀번호를 입력하세요"
                      value={loginForm.password}
                      onChange={e => setLoginForm({ ...loginForm, password: e.target.value })}
                      onKeyDown={e => e.key === 'Enter' && handleLogin()} />
                  </div>
                  <button className="btn btn-primary" style={{ width: '100%', padding: '12px', fontSize: 15, fontWeight: 800 }}
                    onClick={handleLogin} disabled={authLoading}>
                    {authLoading ? '로그인 중...' : '🔑 로그인'}
                  </button>
                  <div style={{ textAlign: 'center', marginTop: 14, fontSize: 13, color: 'var(--text-muted)' }}>
                    계정이 없으신가요?{' '}
                    <button onClick={openRegister} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>회원가입</button>
                  </div>
                </>
              )}

              {/* 회원가입 폼 */}
              {authModal === 'register' && (
                <>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>아이디</label>
                    <input className="form-control" placeholder="아이디를 입력하세요"
                      value={registerForm.username}
                      onChange={e => setRegisterForm({ ...registerForm, username: e.target.value })}
                      autoFocus />
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>이메일 (선택)</label>
                    <input className="form-control" type="email" placeholder="이메일을 입력하세요"
                      value={registerForm.email}
                      onChange={e => setRegisterForm({ ...registerForm, email: e.target.value })} />
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>비밀번호</label>
                    <input className="form-control" type="password" placeholder="비밀번호를 입력하세요"
                      value={registerForm.password}
                      onChange={e => setRegisterForm({ ...registerForm, password: e.target.value })} />
                  </div>
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>비밀번호 확인</label>
                    <input className="form-control" type="password" placeholder="비밀번호를 다시 입력하세요"
                      value={registerForm.password_confirm}
                      onChange={e => setRegisterForm({ ...registerForm, password_confirm: e.target.value })}
                      onKeyDown={e => e.key === 'Enter' && handleRegister()} />
                  </div>
                  <button className="btn btn-primary" style={{ width: '100%', padding: '12px', fontSize: 15, fontWeight: 800 }}
                    onClick={handleRegister} disabled={authLoading}>
                    {authLoading ? '처리 중...' : '✏️ 회원가입'}
                  </button>
                  <div style={{ textAlign: 'center', marginTop: 14, fontSize: 13, color: 'var(--text-muted)' }}>
                    이미 계정이 있으신가요?{' '}
                    <button onClick={openLogin} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>로그인</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 오버레이 (메뉴 닫기) */}
      {menuOpen && (
        <div className="overlay" onClick={() => setMenuOpen(false)} />
      )}
    </div>
  );
};

export default Layout;
